/*
 * ॐ नमः शिवाय
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * SeoEditorModal — per-page SEO editor.
 * Edits meta_title, meta_description, og_title, og_description, og_image_url.
 * Saves via PUT /arcee/application/page/<id>/update/
 */

import React, { useState } from 'react';
import { useTheme } from '../../theme/ThemeContext';
import { updatePage } from '../../hooks/usePages';
import { useQueryClient } from '@tanstack/react-query';

const FIELD_HINTS = {
  meta_title:       { max: 60,  label: 'Meta Title',       hint: 'Shown in browser tab and search results (≤60 chars)' },
  meta_description: { max: 160, label: 'Meta Description',  hint: 'Search result snippet (≤160 chars)' },
  og_title:         { max: 60,  label: 'OG Title',          hint: 'Open Graph title for social shares. Defaults to Meta Title.' },
  og_description:   { max: 200, label: 'OG Description',    hint: 'Open Graph description for social shares.' },
  og_image_url:     { max: 500, label: 'OG Image URL',       hint: 'Absolute URL (https://…) for the social share image (1200×630 recommended).' },
};

function CharCount({ value, max }) {
  const len = (value || '').length;
  const over = len > max;
  return (
    <span style={{ fontSize: 11, color: over ? '#dc2626' : '#94a3b8', float: 'right' }}>
      {len}/{max}
    </span>
  );
}

export default function SeoEditorModal({ pageId, initialSeo = {}, onClose, onSaved }) {
  const { theme } = useTheme();
  const queryClient = useQueryClient();

  const [seo, setSeo] = useState({
    meta_title:       initialSeo.meta_title       || '',
    meta_description: initialSeo.meta_description || '',
    og_title:         initialSeo.og_title         || '',
    og_description:   initialSeo.og_description   || '',
    // Falls back to the legacy `og_image` key so values saved before the
    // rename still load; saving normalises them to `og_image_url`.
    og_image_url:     initialSeo.og_image_url || initialSeo.og_image || '',
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (key, value) => {
    setSeo(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      // Trim every value before saving. A pasted URL routinely carries a
      // leading tab or newline, which survives into the og:image tag and
      // makes crawlers fail to fetch the image.
      const cleaned = Object.fromEntries(
        Object.entries(seo).map(([k, v]) => [k, typeof v === 'string' ? v.trim() : v])
      );
      const updated = await updatePage(pageId, { seo: cleaned });
      queryClient.invalidateQueries({ queryKey: ['page', pageId] });
      if (onSaved) onSaved(updated.seo || seo);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to save SEO settings.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="modal d-block"
      tabIndex="-1"
      style={{ background: 'rgba(0,0,0,0.5)', zIndex: 1050 }}
    >
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content">
          {/* Header */}
          <div className="modal-header" style={{ borderBottom: '1px solid #e2e8f0' }}>
            <div>
              <h5 className="modal-title fw-bold mb-0">
                <i className="bi bi-search me-2" style={{ color: theme.primary }} />
                SEO Settings
              </h5>
              <div className="text-muted small mt-1">
                Configure how this page appears in search results and social shares.
              </div>
            </div>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>

          {/* Body */}
          <div className="modal-body" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
            {error && (
              <div className="alert alert-danger py-2 mb-3">{error}</div>
            )}

            {/* Search preview card */}
            <div
              className="rounded p-3 mb-4"
              style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}
            >
              <div className="text-muted" style={{ fontSize: 11, fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Search Result Preview
              </div>
              <div style={{ color: '#1a0dab', fontSize: 18, fontWeight: 500, lineHeight: 1.3 }}>
                {seo.meta_title || <span className="text-muted">Page Title</span>}
              </div>
              <div style={{ color: '#006621', fontSize: 13, margin: '2px 0' }}>
                https://yoursite.com/p/home
              </div>
              <div style={{ color: '#545454', fontSize: 14, lineHeight: 1.5 }}>
                {seo.meta_description || <span className="text-muted">Meta description will appear here…</span>}
              </div>
            </div>

            {/* Fields */}
            {Object.entries(FIELD_HINTS).map(([key, { max, label, hint }]) => (
              <div key={key} className="mb-4">
                <label className="form-label fw-semibold mb-1" style={{ fontSize: 13 }}>
                  {label}
                  <CharCount value={seo[key]} max={max} />
                </label>
                {key === 'meta_description' || key === 'og_description' ? (
                  <textarea
                    className="form-control form-control-sm"
                    rows={3}
                    value={seo[key]}
                    onChange={e => handleChange(key, e.target.value)}
                    placeholder={hint}
                    maxLength={max + 50}
                  />
                ) : (
                  <input
                    className="form-control form-control-sm"
                    value={seo[key]}
                    onChange={e => handleChange(key, e.target.value)}
                    placeholder={hint}
                    type={key === 'og_image_url' ? 'url' : 'text'}
                    maxLength={max + 50}
                  />
                )}
                <div className="form-text">{hint}</div>
              </div>
            ))}

            {/* OG image preview */}
            {seo.og_image_url && (
              <div className="mb-3">
                <div className="text-muted" style={{ fontSize: 11, fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  OG Image Preview
                </div>
                <img
                  src={seo.og_image_url}
                  alt="OG preview"
                  style={{ maxWidth: '100%', maxHeight: 200, objectFit: 'cover', borderRadius: 8, border: '1px solid #e2e8f0' }}
                  onError={e => { e.target.style.display = 'none'; }}
                />
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="modal-footer" style={{ borderTop: '1px solid #e2e8f0' }}>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn"
              style={{ background: theme.primary, color: '#fff', border: 'none' }}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? (
                <><span className="spinner-border spinner-border-sm me-1" />Saving…</>
              ) : (
                <><i className="bi bi-floppy me-1" />Save SEO</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
